// components/reports/PainPerceptionReport.js
// CRA/MUI port (inline styles) of the marketing site's Pain Perception
// report. Cards are conditional — an old-format Proove report (self-report
// + COMT only, no PSS) or a non-Proove report (foreign_scores only) both
// render correctly without empty placeholder cards.
import React from "react";
import ReportShell from "./ReportShell";
import { PAIN_PERCEPTION_COPY, AI_ANALYSIS_DISCLAIMER_HTML } from "./reportCopy";
import { levelColor, levelBg, colorizeRiskTerms } from "./reportHelpers";
import { sectionBar, innerCard, table, th, td } from "./reportStyles";
import ScoreDial from "./ScoreDial";

function ScoreCard({ label, score, level }) {
  return (
    <div style={{ flex: 1, minWidth: 140, borderRadius: 12, border: "1px solid #e2e2e2", padding: 12, textAlign: "center" }}>
      <p style={{ marginBottom: 4, fontSize: "0.8rem", fontWeight: 600, color: "#575555" }}>{label}</p>
      <div style={{ display: "flex", justifyContent: "center" }}>
        <ScoreDial level={level} score={score} />
      </div>
      <p style={{ marginTop: 4, display: "inline-block", borderRadius: 999, padding: "2px 8px", fontSize: "0.76rem", fontWeight: 700, color: levelColor(level), background: levelBg(level) }}>
        {level || "N/A"}
      </p>
    </div>
  );
}

export default function PainPerceptionReport({ extracted, clinicalSummaryHtml, disclaimer, mismatchNotice, reportDate, variant, analyzedBy }) {
  const selfPain = extracted?.self_reported_pain;
  const pss = extracted?.pss;
  const foreignScores = extracted?.foreign_scores || [];
  const genotypes = extracted?.genotypes || [];
  const hasAnyPrimary = selfPain || foreignScores.length > 0;

  return (
    <ReportShell
      title="Proove Pain Perception"
      subtitle="Proove Pain Perception Profile"
      variant={variant}
      analyzedBy={analyzedBy}
      patient={extracted?.demographics}
      reportDate={reportDate}
      methodology={PAIN_PERCEPTION_COPY.methodology}
      disclaimer={PAIN_PERCEPTION_COPY.disclaimer}
      references={PAIN_PERCEPTION_COPY.references}
      interpretationNode={
        <div style={{ display: "flex", flexDirection: "column", gap: 12, padding: "12px 16px" }}>
          <div>
            <p style={{ marginBottom: 4, fontSize: "0.84rem", fontWeight: 700, color: "#575555" }}>Genotypes</p>
            <p style={{ whiteSpace: "pre-line", fontSize: "0.8rem", lineHeight: 1.6, color: "#575555", margin: 0 }}>
              {PAIN_PERCEPTION_COPY.interpretationGenotypes}
            </p>
          </div>
          <div>
            <p style={{ marginBottom: 4, fontSize: "0.84rem", fontWeight: 700, color: "#575555" }}>Phenotypes</p>
            <p style={{ whiteSpace: "pre-line", fontSize: "0.8rem", lineHeight: 1.6, color: "#575555", margin: 0 }}>
              {PAIN_PERCEPTION_COPY.interpretationPhenotypes}
            </p>
          </div>
        </div>
      }
    >
      {mismatchNotice && (
        <div style={{ marginBottom: 12, borderRadius: 8, border: "1px solid #fcd34d", background: "#fffbeb", padding: "8px 12px", fontSize: "0.82rem", color: "#92400e" }}>
          {mismatchNotice}
        </div>
      )}

      <div style={innerCard}>
        <div style={sectionBar}>Results Summary</div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 12, padding: 12 }}>
          {selfPain && <ScoreCard label="Patient Self-Reported Pain" score={selfPain.score} level={selfPain.level} />}
          {foreignScores.map((fs, i) => (
            <ScoreCard key={i} label={fs.label} level={fs.value} />
          ))}
          {!hasAnyPrimary && (
            <div style={{ flex: 1, borderRadius: 12, border: "1px dashed #e2e2e2", padding: 12, textAlign: "center", fontSize: "0.8rem", color: "#9a9a9a" }}>
              No primary pain score was found in this report.
            </div>
          )}
          {pss && <ScoreCard label="Perceived Stress Scale (PSS)" score={pss.score} level={pss.level} />}
        </div>
      </div>

      <div style={{ ...innerCard, marginTop: 16 }}>
        <div style={sectionBar}>Proove AI Powered-Clinical Summary</div>
        <div style={{ padding: "12px 16px", fontSize: "0.85rem", lineHeight: 1.65, color: "#333" }}>
          {clinicalSummaryHtml ? (
            <div className="ai-summary-html" dangerouslySetInnerHTML={{ __html: colorizeRiskTerms(clinicalSummaryHtml) }} />
          ) : (
            <p style={{ color: "#9a9a9a" }}>AI summary temporarily unavailable — showing extracted results only.</p>
          )}
        </div>
        <div style={{ borderTop: "1px solid #eee", padding: "12px 16px", fontSize: "0.78rem", lineHeight: 1.6, color: "#6b7280" }}
          dangerouslySetInnerHTML={{ __html: disclaimer || AI_ANALYSIS_DISCLAIMER_HTML }} />
      </div>

      {genotypes.length > 0 && (
        <div style={{ ...innerCard, marginTop: 16 }}>
          <table style={table}>
            <thead>
              <tr><th style={th}>Gene</th><th style={th}>RS Number</th><th style={th}>Result</th></tr>
            </thead>
            <tbody>
              {genotypes.map((g, i) => (
                <tr key={i}>
                  <td style={td}>{g.gene}</td>
                  <td style={td}>{g.rs_number || "—"}</td>
                  <td style={td}>{g.result}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div style={{ ...innerCard, marginTop: 16 }}>
        <div style={sectionBar}>Supporting Clinical Evidence</div>
        <p style={{ whiteSpace: "pre-line", padding: "12px 16px", fontSize: "0.78rem", lineHeight: 1.6, color: "#575555" }}>
          <span style={{ fontWeight: 700 }}>Accuracy of the Pain Perception Test: </span>
          {PAIN_PERCEPTION_COPY.accuracyEvidence}
        </p>
        <p style={{ whiteSpace: "pre-line", borderTop: "1px solid #eee", padding: "12px 16px", fontSize: "0.78rem", lineHeight: 1.6, color: "#575555" }}>
          <span style={{ fontWeight: 700 }}>Key Advantages &amp; Benefits: </span>
          {PAIN_PERCEPTION_COPY.advantagesEvidence}
        </p>
      </div>
    </ReportShell>
  );
}
